import { useState, useRef, useMemo } from "react";
import CustomInput, { CustomInputHandle } from "../Input/CustomInput";
import CustomButton from "../Input/CustomButton";
import GoAwayButton from "../Input/GoAwayButton";
import TextYScroller from "./textYScroller";
import LongFormSkeleton from "../Skeletons/LongFormSkeleton";
import {
	useContacts,
	useAddContact,
	useRemoveContact
} from "../../../services/api-client/useContacts";
import { useSnackbar } from "../../../services/api-client/useSnackbar";

interface ContactsTabProps {
	jobId: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINKEDIN_PREFIX = "https://www.linkedin.com/";

const ContactsTab: React.FC<ContactsTabProps> = ({ jobId }) => {
	const [contactName, setContactName] = useState("");
	const [contactRole, setContactRole] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [contactLinkedIn, setContactLinkedIn] = useState("");
	const [formKey, setFormKey] = useState(0);

	const nameRef = useRef<CustomInputHandle>(null);
	const emailRef = useRef<CustomInputHandle>(null);
	const linkedInRef = useRef<CustomInputHandle>(null);

	const { contacts, isLoading } = useContacts(jobId);
	const addContact = useAddContact(jobId);
	const removeContact = useRemoveContact(jobId);
	const { showSnack } = useSnackbar();

	const sortedContacts = useMemo(
		() =>
			[...contacts].sort((a, b) => a.contactName.localeCompare(b.contactName)),
		[contacts]
	);

	const handleAddContact = async () => {
		// Trigger validation on all fields with validators
		const nameValid = nameRef.current?.triggerValidate() ?? false;
		const emailValid = emailRef.current?.triggerValidate() ?? true;
		const linkedInValid = linkedInRef.current?.triggerValidate() ?? true;

		if (!nameValid || !emailValid || !linkedInValid) return;

		const payload: {
			contactName: string;
			contactRole?: string;
			contactEmail?: string;
			contactLinkedIn?: string;
		} = {
			contactName: contactName.trim()
		};
		if (contactRole.trim()) payload.contactRole = contactRole.trim();
		if (contactEmail.trim()) payload.contactEmail = contactEmail.trim();
		if (contactLinkedIn.trim())
			payload.contactLinkedIn = contactLinkedIn.trim();

		await addContact.mutateAsync(payload);
		showSnack("NotifyContactAdded", "dark");

		// Reset form
		setContactName("");
		setContactRole("");
		setContactEmail("");
		setContactLinkedIn("");
		setFormKey((k) => k + 1);
	};

	const handleRemoveContact = async (contactId: number) => {
		await removeContact.mutateAsync(contactId);
	};

	if (isLoading) {
		return <LongFormSkeleton className="min-h-96" />;
	}

	return (
		<div className="space-y-4" key={formKey}>
			<CustomInput
				ref={nameRef}
				label="Name"
				onChange={(value) => setContactName(value)}
				onValidate={(value) => value.trim().length > 0}
				validationText="Name is required"
			/>
			<CustomInput label="Role" onChange={(value) => setContactRole(value)} />
			<CustomInput
				ref={emailRef}
				label="Email"
				onChange={(value) => setContactEmail(value)}
				onValidate={(value) => value.trim() === "" || EMAIL_REGEX.test(value)}
				validationText="Please enter a valid email address"
			/>
			<CustomInput
				ref={linkedInRef}
				label="LinkedIn URL"
				onChange={(value) => setContactLinkedIn(value)}
				onValidate={(value) =>
					value.trim() === "" || value.startsWith(LINKEDIN_PREFIX)
				}
				validationText="Please enter a valid LinkedIn URL"
			/>

			<div className="flex justify-end">
				<CustomButton
					text="Add Contact"
					rightIcon="External"
					disabled={!contactName.trim()}
					onClick={handleAddContact}
				/>
			</div>

			<TextYScroller
				tailClassMinHeight="min-h-0"
				tailClassMaxHeight="max-h-[calc(100vh-500px)]"
			>
				{sortedContacts.map((contact) => (
					<div
						key={contact.contactId}
						className="flex items-center justify-between py-3 border-b border-primary-accent"
					>
						<div>
							<p className="text-sm text-primary font-bold">
								{contact.contactName}
								{contact.contactRole && (
									<span className="font-normal text-primary">
										{" "}
										· {contact.contactRole}
									</span>
								)}
							</p>
							{contact.contactEmail && (
								<p className="text-xs text-primary">{contact.contactEmail}</p>
							)}
							{contact.contactLinkedIn && (
								<p className="text-xs text-primary">
									{contact.contactLinkedIn}
								</p>
							)}
						</div>
						<GoAwayButton
							onClick={() => handleRemoveContact(contact.contactId)}
							className="stroke-primary-inactive hover:stroke-primary-active"
						/>
					</div>
				))}
			</TextYScroller>
		</div>
	);
};

export default ContactsTab;
